import { Trans } from '@lingui/macro';
import {
  ActionIcon,
  Box,
  Button,
  Divider,
  Grid,
  LoadingOverlay,
  Stack,
  Text,
} from '@mantine/core';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'tabler-icons-react';

import { TimeBlock } from '../../components/train-route-info/train-route-info';
import { UiCard } from '../../components/ui/ui-card/ui-card';
import { MainSideMenu } from '../../features/main-side-menu/main-side-menu';
import { getNameLocal } from '../../lib/local/get-title';
import { useLocal } from '../../lib/local/use-local';
import { routeCabinet } from '../../router/routes';
import { useOrders } from '../cabinet/features/orders/orders.provider';
import { UserTimeZoneNotify } from '../tz-notify/tz-notify';
import { DisplayFeedback } from './display-feedback';
import { useOrdersFeedback } from './feedback-orders.store';

import styles from './reviews.page.module.css';

export const ReviewsPage = observer(() => {
  const [lang] = useLocal();
  const navigate = useNavigate();
  const { getOrdersRequest, ordersStore } = useOrders();

  const isLoading = getOrdersRequest.isWaitingForLoad;

  const feedbackOrders = useOrdersFeedback({ ordersStore });

  const waitingFeedback = feedbackOrders.feedbackOrders.get().waitingFeedback;
  const withFeedback = feedbackOrders.feedbackOrders.get().withFeedback;

  const isEmptyTrips = ordersStore.tickets.length === 0 && !isLoading;

  if (isEmptyTrips) {
    return (
      <div className={styles.container}>
        <header className={styles.header}>
          <div className={styles.backButton}>
            <ActionIcon
              variant="transparent"
              radius="50%"
              size="40px"
              bg="transparent"
              onClick={() => navigate(routeCabinet.path)}
            >
              <ArrowLeft size={24} strokeWidth={2} color="white" />
            </ActionIcon>
          </div>
          <div className={styles.routeTitle}>
            <h1 className={styles.headerTitle}>
              <Trans>Отзывы</Trans>
            </h1>
          </div>
          <div className={styles.mainSideMenu}>
            <MainSideMenu />
          </div>
        </header>

        <main className={styles.main}>
          <Box pos="relative" mih={200}>
            <LoadingOverlay visible={isLoading} />

            <UiCard>
              <Trans>Поездки пока что отсутствуют</Trans>
            </UiCard>

            <UserTimeZoneNotify />
          </Box>
        </main>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.backButton}>
          <ActionIcon
            variant="transparent"
            radius="50%"
            size="40px"
            bg="transparent"
            onClick={() => navigate(routeCabinet.path)}
          >
            <ArrowLeft size={24} strokeWidth={2} color="white" />
          </ActionIcon>
        </div>
        <div className={styles.routeTitle}>
          <h1 className={styles.headerTitle}>
            <Trans>Отзывы</Trans>
          </h1>
        </div>
        <div className={styles.mainSideMenu}>
          <MainSideMenu />
        </div>
      </header>

      <main className={styles.main}>
        <Box pos="relative" mih={200}>
          <LoadingOverlay visible={isLoading} />

          {ordersStore.tickets.length === 0 && !isLoading && (
            <UiCard>
              <Trans>Поездки пока что отсутствуют</Trans>
            </UiCard>
          )}

          <UserTimeZoneNotify />

          {withFeedback.length === 0 && withFeedback.length === 0 ? (
            <UiCard>
              <Trans>
                У вас нет отзывов о поездках или нет поездок, которые можно
                оценить.
              </Trans>
            </UiCard>
          ) : null}

          <Stack spacing="sm">
            {waitingFeedback.length === 0 ? null : (
              <Text fz="xl" fw="bold">
                <Trans>Поездки без отзыва</Trans>
              </Text>
            )}

            {waitingFeedback.map((order) => (
              <UiCard key={order.orderId}>
                <Grid align="center" justify="center" gutter={10}>
                  <Grid.Col span="content">
                    <TimeBlock
                      target={getNameLocal(lang, order.stationFrom)}
                      date={new Date(order.departureTime)}
                    />
                  </Grid.Col>
                  <Grid.Col span="auto">
                    <Divider my={4} />
                  </Grid.Col>
                  <Grid.Col span="content">
                    <TimeBlock
                      target={getNameLocal(lang, order.stationTo)}
                      date={new Date(order.arrivalTime)}
                      direction="end"
                    />
                  </Grid.Col>
                </Grid>

                <Button
                  size="lg"
                  mt={16}
                  fullWidth
                  onClick={() =>
                    navigate(
                      routeCabinet.TICKET.buildPath(
                        { id: order.orderId },
                        {},
                        'feedback'
                      )
                    )
                  }
                >
                  <Trans>Оставить отзыв</Trans>
                </Button>
              </UiCard>
            ))}

            {withFeedback.length === 0 ? null : (
              <Text fz="xl" fw="bold">
                <Trans>Оцененные поездки</Trans>
              </Text>
            )}

            {withFeedback.map((order) => (
              <UiCard key={order.orderId}>
                <Grid align="center" justify="center" gutter={10}>
                  <Grid.Col span="content">
                    <TimeBlock
                      target={getNameLocal(lang, order.stationFrom)}
                      date={new Date(order.departureTime)}
                    />
                  </Grid.Col>
                  <Grid.Col span="auto">
                    <Divider my={4} />
                  </Grid.Col>
                  <Grid.Col span="content">
                    <TimeBlock
                      target={getNameLocal(lang, order.stationTo)}
                      date={new Date(order.arrivalTime)}
                      direction="end"
                    />
                  </Grid.Col>
                </Grid>

                <Box>
                  <DisplayFeedback feedback={order} />
                </Box>

                <Button
                  size="lg"
                  mt={16}
                  fullWidth
                  onClick={() =>
                    navigate(
                      routeCabinet.TICKET.buildPath(
                        { id: order.orderId },
                        {},
                        'feedback'
                      )
                    )
                  }
                >
                  <Trans>Посмотреть отзыв</Trans>
                </Button>
              </UiCard>
            ))}
          </Stack>
        </Box>
      </main>
    </div>
  );
});
